import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import type { FormEvent } from 'react';

import { handOnTargetsQuery, type JobLine, type HandOnTarget, unitLabel } from '../api/jobs.ts';
import { dayLabel } from '../api/quotes.ts';
import { Button } from '../components/button.tsx';
import { Field } from '../components/field.tsx';

// CB-REQ INV-A13 at v1.957: "Hand on to next job". Gear coming back on this line goes straight to
// another job's line — checked in here and booked out there in one step — instead of home to the
// depot. The next job then holds it from now, so it is never free overnight between the two.
// Whether it fits, and whether anything else holds it in the gap, is the server's to say. A job whose
// line is only pencilled is listed with what to do about it, not as a choice, rather than left out
// (the operator's decision at slice 3's checks, 2026-10-01).

export type HandOnInput = { toJobId: string; toLineId: string } & (
  { quantity: number } | { assetIds: string[] }
);

export function HandOnPanel({
  jobId,
  line,
  pending,
  error,
  onSubmit,
  onClose,
}: {
  jobId: string;
  line: JobLine;
  pending: boolean;
  error: Error | null;
  onSubmit: (input: HandOnInput) => void;
  onClose: () => void;
}) {
  const panel = useRef<HTMLDivElement>(null);
  // Below the lines, like the check-in panel; on a phone that is below the fold.
  useEffect(() => {
    panel.current?.scrollIntoView?.({ block: 'nearest' });
  }, []);
  const targets = useQuery(handOnTargetsQuery(jobId, line.id));
  const [chosen, setChosen] = useState<string | null>(null);
  const open = targets.data?.filter((t) => !t.pencilled) ?? [];
  const pencilled = targets.data?.filter((t) => t.pencilled) ?? [];
  const target = open.find((t) => t.lineId === chosen) ?? open[0] ?? null;

  return (
    <div ref={panel} className="flex flex-col gap-3 border-2 border-divider p-3">
      <div className="flex items-center justify-between gap-3">
        <p className="text-sm font-semibold">
          Hand {line.description} on to the next job: {line.dispatch.out} still out
        </p>
        <Button type="button" variant="quiet" onClick={onClose}>
          Done
        </Button>
      </div>
      {targets.isPending ? <p className="text-sm text-muted">Loading…</p> : null}
      {targets.isError ? (
        <p role="alert" className="text-sm font-semibold text-accent-2-700">
          {targets.error.message}
        </p>
      ) : null}
      {targets.data?.length === 0 ? (
        <p className="text-sm text-ink-soft">
          No other job has this still to go. Check it in instead.
        </p>
      ) : null}
      {target ? (
        <>
          <fieldset className="flex flex-col gap-1.5">
            <legend className="mb-1 text-xs text-ink-soft">To which job</legend>
            {open.map((t) => (
              <label key={t.lineId} className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="hand-on-target"
                  checked={t.lineId === target.lineId}
                  onChange={() => setChosen(t.lineId)}
                />
                <TargetLabel target={t} />
              </label>
            ))}
          </fieldset>
          {line.serialised ? (
            <UnitHandOn
              key={target.lineId}
              line={line}
              target={target}
              pending={pending}
              onSubmit={onSubmit}
            />
          ) : (
            <CountHandOn
              key={target.lineId}
              line={line}
              target={target}
              pending={pending}
              onSubmit={onSubmit}
            />
          )}
        </>
      ) : null}
      {pencilled.length > 0 ? (
        <div className="flex flex-col gap-1.5">
          <p className="text-xs text-ink-soft">
            Only pencilled, so it can’t take gear yet — reserve it there first
          </p>
          <ul className="flex flex-col gap-1.5">
            {pencilled.map((t) => (
              <li key={t.lineId} className="text-sm">
                <Link to="/jobs/$jobId" params={{ jobId: t.jobId }} className="link font-semibold">
                  {t.reference}
                </Link>{' '}
                <TargetLabel target={t} named={false} />
              </li>
            ))}
          </ul>
        </div>
      ) : null}
      {error ? (
        <p role="alert" className="text-sm font-semibold text-accent-2-700">
          {error.message}
        </p>
      ) : null}
    </div>
  );
}

function TargetLabel({ target, named = true }: { target: HandOnTarget; named?: boolean }) {
  const start = target.scheduledWindow.localStart;
  return (
    <span>
      {named ? <span className="font-semibold">{target.reference} </span> : null}
      {target.name}
      <span className="text-muted">
        {' '}
        · {target.venue ?? 'no venue'} · goes out {dayLabel(start.slice(0, 10))}{' '}
        {start.slice(11, 16)}
        {target.pencilled ? '' : ` · ${target.toGo} to go`}
      </span>
    </span>
  );
}

function CountHandOn({
  line,
  target,
  pending,
  onSubmit,
}: {
  line: JobLine;
  target: HandOnTarget;
  pending: boolean;
  onSubmit: (input: HandOnInput) => void;
}) {
  const [count, setCount] = useState(String(Math.min(line.dispatch.out, target.toGo)));
  const [problem, setProblem] = useState<string | undefined>();

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!/^\d+$/.test(count.trim()) || Number(count) === 0) {
      return setProblem('How many are handed on — a whole number.');
    }
    if (Number(count) > line.dispatch.out)
      return setProblem(`Only ${line.dispatch.out} are still out.`);
    setProblem(undefined);
    onSubmit({ toJobId: target.jobId, toLineId: target.lineId, quantity: Number(count) });
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-3">
      <div className="sm:max-w-40">
        <Field
          label="How many"
          inputMode="numeric"
          value={count}
          error={problem}
          onChange={(e) => setCount(e.currentTarget.value)}
        />
      </div>
      <p className="text-xs text-muted">
        Only what came back in good order. It keeps to where it came from: depot gear fills the next
        job’s depot share. Check in anything else as usual.
      </p>
      <div>
        <Button type="submit" disabled={pending}>
          Hand on to {target.reference}
        </Button>
      </div>
    </form>
  );
}

function UnitHandOn({
  line,
  target,
  pending,
  onSubmit,
}: {
  line: JobLine;
  target: HandOnTarget;
  pending: boolean;
  onSubmit: (input: HandOnInput) => void;
}) {
  const out = line.allocated.filter((u) => u.dispatch === 'out');
  const [chosen, setChosen] = useState<string[]>([]);
  const [problem, setProblem] = useState<string | undefined>();

  function submit(event: FormEvent) {
    event.preventDefault();
    // Said, not a greyed button (CB-REQ ADO-06).
    if (chosen.length === 0) return setProblem('Choose the units to hand on.');
    setProblem(undefined);
    onSubmit({ toJobId: target.jobId, toLineId: target.lineId, assetIds: chosen });
  }

  return (
    <form onSubmit={submit} className="flex flex-col gap-3">
      <fieldset className="flex flex-col gap-1.5">
        <legend className="mb-1 text-xs text-ink-soft">Which units, in good order</legend>
        {out.map((u) => (
          <label key={u.assetId} className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={chosen.includes(u.assetId)}
              onChange={(e) => {
                const on = e.currentTarget.checked;
                setChosen((c) => (on ? [...c, u.assetId] : c.filter((id) => id !== u.assetId)));
              }}
            />
            <span className="font-semibold">{unitLabel(u)}</span>
          </label>
        ))}
      </fieldset>
      {problem ? (
        <p role="alert" className="text-sm font-semibold text-accent-2-700">
          {problem}
        </p>
      ) : null}
      <div>
        <Button type="submit" disabled={pending}>
          Hand on {chosen.length > 1 ? `${chosen.length} units ` : ''}to {target.reference}
        </Button>
      </div>
    </form>
  );
}
